/**
 * Workflow Commit Module
 *
 * Provides commit/archive actions for workflow entities with callback
 * registration for playground-specific orchestration.
 *
 * The workflow API returns new revision IDs directly — no polling needed.
 *
 * @example
 * ```typescript
 * import { commitWorkflowRevisionAtom, registerWorkflowCommitCallbacks } from '@agenta/entities/workflow'
 *
 * registerWorkflowCommitCallbacks({
 *   onQueryInvalidate: async () => { ... },
 *   onNewRevision: async (result) => { ... },
 * })
 *
 * const result = await set(commitWorkflowRevisionAtom, {
 *   revisionId: currentId,
 *   commitMessage: 'Updated parameters',
 * })
 * ```
 */

import {projectIdAtom} from "@agenta/shared/state"
import {extractApiErrorMessage, preserveResponseStatus} from "@agenta/shared/utils"
import isEqual from "fast-deep-equal"
import {atom, getDefaultStore} from "jotai"

import {
    commitWorkflowRevisionApi,
    createWorkflow as createWorkflowApi,
    createWorkflowVariantApi,
    archiveWorkflowRevision,
    archiveWorkflowVariant,
    queryWorkflowRevisions,
} from "../api"
import {generateSlug, type Workflow} from "../core"

import {workflowsListDataAtom} from "./allWorkflows"
import {
    buildCreatePayloadFromEphemeral,
    prepareCommitParameters,
    prepareCommitSchemas,
} from "./createPayload"
import {invalidateEvaluatorsListCache} from "./evaluatorUtils"
import {
    workflowEntityAtomFamily,
    workflowDraftAtomFamily,
    updateWorkflowDraftAtom,
    consumeWorkflowDraftAtom,
    transferBuildKitStateAtom,
    migrateBuildKitStateAtom,
    invalidateWorkflowsListCache,
    invalidateWorkflowCache,
    invalidateWorkflowRevisionsByWorkflowCache,
    invalidateWorkflowRevisionsByVariantCache,
    invalidateWorkflowVariantsCache,
    primeWorkflowRevisionDetailCacheImperative,
    primeCommittedRevisionRefLists,
    getFlatSourceData,
} from "./store"

// ============================================================================
// TYPES
// ============================================================================

/**
 * Parameters for committing a workflow revision
 */
export interface WorkflowCommitParams {
    /** Current revision ID */
    revisionId: string
    /** Commit message */
    commitMessage?: string
}

/**
 * Result of a successful commit
 */
export interface WorkflowCommitResult {
    success: true
    /** The source revision ID */
    revisionId: string
    /** The new revision ID */
    newRevisionId: string
    /** The new workflow data */
    workflow?: Workflow
}

/**
 * Result of a failed commit
 */
export interface WorkflowCommitError {
    success: false
    error: Error
}

export type WorkflowCommitOutcome = WorkflowCommitResult | WorkflowCommitError

/**
 * Parameters for archiving a workflow revision
 */
export interface WorkflowArchiveParams {
    /** Revision ID to archive */
    revisionId: string
    /** Workflow ID (parent workflow artifact) */
    workflowId: string
    /** Variant ID (parent variant) — when provided, the atom checks if the
     *  variant has remaining active revisions and archives it if empty. */
    variantId?: string
}

/**
 * Result of an archive operation
 */
export interface WorkflowArchiveResult {
    success: true
    revisionId: string
    workflowId: string
}

export interface WorkflowArchiveError {
    success: false
    error: Error
}

export type WorkflowArchiveOutcome = WorkflowArchiveResult | WorkflowArchiveError

// ============================================================================
// COMMIT CALLBACK REGISTRY
// ============================================================================

export interface WorkflowCommitCallbacks {
    onQueryInvalidate?: () => Promise<void>
    onNewRevision?: (result: WorkflowCommitResult, params: WorkflowCommitParams) => Promise<void>
    onError?: (error: Error, params: WorkflowCommitParams) => void
}

let _commitCallbacks: WorkflowCommitCallbacks = {}

export function registerWorkflowCommitCallbacks(callbacks: WorkflowCommitCallbacks): void {
    _commitCallbacks = {..._commitCallbacks, ...callbacks}
}

export function getWorkflowCommitCallbacks(): Readonly<WorkflowCommitCallbacks> {
    return _commitCallbacks
}

export function clearWorkflowCommitCallbacks(): void {
    _commitCallbacks = {}
}

/**
 * Invoke the registered onNewRevision callback.
 *
 * Used by flows that bypass `commitWorkflowRevisionAtom` (e.g., creating
 * a new workflow for ephemeral entities) but still need to notify listeners
 * like the CreateEvaluatorDrawer.
 */
export async function invokeWorkflowCommitCallbacks(
    result: WorkflowCommitResult,
    params: WorkflowCommitParams,
): Promise<void> {
    if (_commitCallbacks.onNewRevision) {
        await _commitCallbacks.onNewRevision(result, params)
    }
}

// ============================================================================
// ARCHIVE CALLBACK REGISTRY
// ============================================================================

export interface WorkflowArchiveCallbacks {
    onQueryInvalidate?: () => Promise<void>
    onRevisionDeleted?: (result: WorkflowArchiveResult) => Promise<void>
    onError?: (error: Error, params: WorkflowArchiveParams) => void
}

let _archiveCallbacks: WorkflowArchiveCallbacks = {}

export function registerWorkflowArchiveCallbacks(callbacks: WorkflowArchiveCallbacks): void {
    _archiveCallbacks = {..._archiveCallbacks, ...callbacks}
}

export function clearWorkflowArchiveCallbacks(): void {
    _archiveCallbacks = {}
}

// ============================================================================
// COMMIT ATOM
// ============================================================================

/**
 * Commit a workflow revision to create a new version.
 *
 * Flow:
 * 1. Read merged entity data (server + draft)
 * 2. Call updateWorkflow API (returns new revision directly)
 * 3. Invalidate caches
 * 4. Invoke callbacks
 * 5. Discard draft
 */
export const commitWorkflowRevisionAtom = atom(
    null,
    async (get, set, params: WorkflowCommitParams): Promise<WorkflowCommitOutcome> => {
        const {revisionId, commitMessage: _commitMessage} = params
        set(migrateBuildKitStateAtom, revisionId)

        try {
            const projectId = get(projectIdAtom)
            if (!projectId) {
                throw new Error("No project ID available")
            }

            // 1. Read merged entity data
            const entity = get(workflowEntityAtomFamily(revisionId))
            if (!entity) {
                throw new Error(`No workflow entity found for ${revisionId}`)
            }
            // What we are about to send. Step 4 compares against this so an edit made WHILE the
            // request is in flight is not mistaken for something this commit contains.
            const draftAtSend = get(workflowDraftAtomFamily(revisionId))
            const flatSource = getFlatSourceData(get, revisionId)
            const flatParams =
                (flatSource?.data?.parameters as Record<string, unknown> | null) ?? null
            const flatSchemas = flatSource?.data?.schemas ?? null

            // 2. Call the revision commit endpoint directly.
            // We do NOT use `updateWorkflow` here — that function also fires a
            // `PUT /workflows/{id}` for metadata edits, which overwrites
            // the artifact's `flags` with null and causes the app to vanish from
            // the apps list.
            const workflowId = entity.workflow_id ?? entity.id
            const variantId = entity.workflow_variant_id ?? entity.variant_id

            if (!entity.data) {
                throw new Error("Cannot commit workflow: no data to commit")
            }

            const newWorkflow = await commitWorkflowRevisionApi(projectId, {
                workflowId,
                variantId: variantId ?? undefined,
                name: entity.name ?? undefined,
                message: _commitMessage ?? undefined,
                // All WorkflowData fields (backend forbids extras), with
                // parameters/schemas prepared.
                data: {
                    uri: entity.data.uri,
                    url: entity.data.url,
                    headers: entity.data.headers,
                    script: entity.data.script,
                    runtime: entity.data.runtime,
                    parameters: prepareCommitParameters(entity, flatParams),
                    schemas: prepareCommitSchemas(entity, flatSchemas),
                },
            })

            const newRevisionId = newWorkflow.id

            // Prime the per-revision detail cache with the server response
            // BEFORE invoking callbacks (which may router.push to the
            // playground page). Without this, the playground mounts and
            // queries the new revision via `workflowQueryAtomFamily`,
            // which has to make a fresh network round-trip. On slower
            // environments (preview deployments) the query is still pending
            // by the time `displayedEntityIdsAtom` evaluates and SUB 4 in
            // `playgroundSyncAtom` falls back to `revisions[0]` (the v0
            // seed). Priming the cache here makes the query resolve
            // synchronously with `initialData`, keeping the just-created
            // revision selected through the navigation.
            primeWorkflowRevisionDetailCacheImperative(newWorkflow)
            // The pickers list thin refs; seed the new one so the dropdown shows it immediately
            // rather than after the invalidation below refetches.
            primeCommittedRevisionRefLists(newWorkflow)

            const result: WorkflowCommitResult = {
                success: true,
                revisionId,
                newRevisionId,
                workflow: newWorkflow,
            }

            // 3. Invoke new revision callback (entity switch — must happen
            // before returning so the UI shows the correct entity)
            if (_commitCallbacks.onNewRevision) {
                await _commitCallbacks.onNewRevision(result, params)
            }

            // 4. Discard only what this commit contains. The payload was snapshotted before the
            // `await`, so an edit typed during the request is in neither the new revision nor —
            // once discarded — anywhere else. Carry it onto the new revision instead.
            const draftNow = get(workflowDraftAtomFamily(revisionId))
            const editedDuringCommit = !isEqual(draftNow, draftAtSend)
            let carriedForward = false
            if (editedDuringCommit && draftNow?.data) {
                set(updateWorkflowDraftAtom, newRevisionId, {data: draftNow.data})
                // Dropped when the new revision has no server baseline yet — confirm, don't assume.
                carriedForward = get(workflowDraftAtomFamily(newRevisionId)) !== null
            }
            // A stranded draft is recoverable; a discarded one is not.
            if (!editedDuringCommit || carriedForward) {
                // CONSUMED, not discarded. A host that switches to the new revision after this
                // returns still has the old one on screen, and a surface that resets itself on a
                // discard would reset here for a commit nobody asked for (D94).
                set(consumeWorkflowDraftAtom, revisionId)
            }

            // 5. Invalidate caches in the background so the caller (modal)
            // isn't blocked by network refetches.
            invalidateWorkflowsListCache()
            invalidateEvaluatorsListCache()
            invalidateWorkflowCache(revisionId)
            invalidateWorkflowRevisionsByWorkflowCache(workflowId)
            // The picker lists by VARIANT, so without this the revision just created is missing
            // from the dropdown until something else refetches.
            if (variantId) invalidateWorkflowRevisionsByVariantCache(variantId)
            if (_commitCallbacks.onQueryInvalidate) {
                void _commitCallbacks.onQueryInvalidate()
            }

            return result
        } catch (error) {
            const err = new Error(extractApiErrorMessage(error))

            if (_commitCallbacks.onError) {
                _commitCallbacks.onError(err, params)
            }

            return {
                success: false,
                error: err,
            }
        }
    },
)

// ============================================================================
// CREATE VARIANT ATOM
// ============================================================================

/**
 * Parameters for creating a new workflow variant
 */
export interface WorkflowCreateVariantParams {
    /** Source revision ID (to copy data from) */
    baseRevisionId: string
    /** Name for the new variant */
    newVariantName: string
    /** Slug suffix for the new variant. The workflow slug prefix is preserved internally. */
    slug?: string
    /** Commit message for the first revision */
    commitMessage?: string
}

/**
 * Result of a successful variant creation
 */
export interface WorkflowCreateVariantResult {
    success: true
    /** The new revision ID (first revision under the new variant) */
    newRevisionId: string
    /** The new variant ID */
    newVariantId: string
}

export interface WorkflowCreateVariantError {
    success: false
    error: Error
}

export type WorkflowCreateVariantOutcome = WorkflowCreateVariantResult | WorkflowCreateVariantError

/**
 * Create a new workflow variant and commit its first revision with the
 * current entity data.
 *
 * Flow:
 * 1. Read the source entity data (server + draft)
 * 2. Create a new variant via `POST /workflows/variants/`
 * 3. Commit first revision under the new variant via `POST /workflows/revisions/commit`
 * 4. Invalidate caches
 * 5. Invoke callbacks (reuses commit callbacks for query invalidation and switch)
 */
export const createWorkflowVariantAtom = atom(
    null,
    async (
        get,
        set,
        params: WorkflowCreateVariantParams,
    ): Promise<WorkflowCreateVariantOutcome> => {
        const {baseRevisionId, newVariantName, slug: explicitSlug, commitMessage} = params
        set(migrateBuildKitStateAtom, baseRevisionId)

        try {
            const projectId = get(projectIdAtom)
            if (!projectId) {
                throw new Error("No project ID available")
            }

            // 1. Read the source entity data
            const entity = get(workflowEntityAtomFamily(baseRevisionId))
            if (!entity) {
                throw new Error(`No workflow entity found for ${baseRevisionId}`)
            }
            const flatSource = getFlatSourceData(get, baseRevisionId)
            const flatParams =
                (flatSource?.data?.parameters as Record<string, unknown> | null) ?? null
            const flatSchemas = flatSource?.data?.schemas ?? null

            const workflowId = entity.workflow_id ?? entity.id
            if (!entity.data) {
                throw new Error("Cannot create variant: no data to carry over")
            }

            // 2. Create the new variant
            const allWorkflows = get(workflowsListDataAtom)
            const workflowArtifact = allWorkflows.find((w) => w.id === workflowId)
            const variantSlugSuffix = generateSlug(newVariantName)
            const requestedSlug = explicitSlug || variantSlugSuffix
            const slug =
                workflowArtifact?.slug && !requestedSlug.startsWith(`${workflowArtifact.slug}.`)
                    ? `${workflowArtifact.slug}.${requestedSlug}`
                    : requestedSlug
            const newVariant = await createWorkflowVariantApi(projectId, {
                workflowId,
                slug,
                name: newVariantName,
            })

            if (!newVariant?.id) {
                throw new Error("Failed to create workflow variant")
            }

            // 3. Commit seed revision (v0) — matches legacy behavior where
            //    fork always creates v0 + v1 so the user-visible first version is v1
            await commitWorkflowRevisionApi(projectId, {
                workflowId,
                variantId: newVariant.id,
                name: newVariantName,
                data: {
                    uri: entity.data.uri,
                    url: entity.data.url,
                },
            })

            // 4. Commit actual data revision (v1) with full data
            const newRevision = await commitWorkflowRevisionApi(projectId, {
                workflowId,
                variantId: newVariant.id,
                name: newVariantName,
                message: commitMessage,
                data: {
                    uri: entity.data.uri,
                    url: entity.data.url,
                    headers: entity.data.headers,
                    script: entity.data.script,
                    runtime: entity.data.runtime,
                    parameters: prepareCommitParameters(entity, flatParams),
                    schemas: prepareCommitSchemas(entity, flatSchemas),
                },
            })

            const newRevisionId = newRevision.id

            // Prime the per-revision detail cache before callbacks run —
            // see the matching comment in `commitWorkflowRevisionAtom`.
            // This covers the "commit as new variant" flow, which also
            // triggers post-commit navigation to the new revision.
            primeWorkflowRevisionDetailCacheImperative(newRevision)

            // 5. Invoke new revision callback (entity switch — must happen
            // before returning so the UI shows the correct entity)
            const commitResult: WorkflowCommitResult = {
                success: true,
                revisionId: baseRevisionId,
                newRevisionId,
                workflow: newRevision,
            }
            if (_commitCallbacks.onNewRevision) {
                await _commitCallbacks.onNewRevision(commitResult, {
                    revisionId: baseRevisionId,
                    commitMessage,
                })
            }

            // CONSUMED, not discarded: the edits became a revision. See `consumeWorkflowDraftAtom`.
            set(consumeWorkflowDraftAtom, baseRevisionId)

            // 6. Invalidate caches in the background so the caller (modal)
            // isn't blocked by network refetches.
            invalidateWorkflowsListCache()
            invalidateEvaluatorsListCache()
            invalidateWorkflowCache(baseRevisionId)
            invalidateWorkflowRevisionsByWorkflowCache(workflowId)
            invalidateWorkflowVariantsCache(workflowId)
            if (_commitCallbacks.onQueryInvalidate) {
                void _commitCallbacks.onQueryInvalidate()
            }

            return {
                success: true,
                newRevisionId,
                newVariantId: newVariant.id,
            }
        } catch (error) {
            const err = preserveResponseStatus(error, extractApiErrorMessage(error))
            return {
                success: false,
                error: err,
            }
        }
    },
)

// ============================================================================
// CREATE FROM EPHEMERAL ATOM
// ============================================================================

/**
 * Parameters for creating a workflow from an ephemeral (local-only) entity
 */
export interface WorkflowCreateFromEphemeralParams {
    /** Local entity ID of the ephemeral workflow */
    revisionId: string
    /** Commit message */
    commitMessage?: string
    /** Display name for the new workflow (overrides entity name) */
    name?: string
    /** Slug for the new workflow. When provided, overrides auto-generation. */
    slug?: string
}

/**
 * Create a new workflow from an ephemeral (local-only) entity.
 *
 * Ephemeral entities are created from templates and have `meta.__ephemeral: true`.
 * They have no server baseline — this atom creates the workflow artifact and
 * commits the initial revision.
 *
 * Flow:
 * 1. Read ephemeral entity data from the store
 * 2. Generate a unique slug (NOT the template key)
 * 3. Call createWorkflow API (creates artifact + commits revision)
 * 4. Invoke commit callbacks
 * 5. Discard local draft
 * 6. Invalidate caches
 */
export const createWorkflowFromEphemeralAtom = atom(
    null,
    async (get, set, params: WorkflowCreateFromEphemeralParams): Promise<WorkflowCommitOutcome> => {
        const {revisionId, commitMessage, name, slug} = params

        try {
            const projectId = get(projectIdAtom)
            if (!projectId) {
                throw new Error("No project ID available")
            }

            // 1. Read the exact payload that ordinary ephemeral creation commits.
            const {entity, data, flags} = buildCreatePayloadFromEphemeral(get, revisionId)

            // 2. Generate a unique slug (never use the template key)
            const workflowName = name || entity.name || "Workflow"
            const workflowSlug = slug || generateSlug(workflowName)

            // 3. Create workflow via API
            const newWorkflow = await createWorkflowApi(projectId, {
                slug: workflowSlug,
                name: workflowName,
                flags,
                message: commitMessage || undefined,
                data,
            })

            const newRevisionId = newWorkflow.id
            if (newWorkflow.workflow_id)
                set(transferBuildKitStateAtom, {revisionId, workflowId: newWorkflow.workflow_id})

            // Prime the per-revision detail cache before callbacks run —
            // see the matching comment in `commitWorkflowRevisionAtom`.
            // Critical for the evaluator-create + code-eval-create flows
            // where the drawer wrapper router.pushes to the new revision's
            // playground URL; without this prime the playground would fall
            // back to the v0 seed revision on slower environments.
            primeWorkflowRevisionDetailCacheImperative(newWorkflow)

            const result: WorkflowCommitResult = {
                success: true,
                revisionId,
                newRevisionId,
                workflow: newWorkflow,
            }

            // 4. Invoke commit callbacks (reuse shared helper)
            await invokeWorkflowCommitCallbacks(result, {revisionId, commitMessage})

            // 5. The commit consumed the local draft; it was not discarded.
            set(consumeWorkflowDraftAtom, revisionId)

            // 6. Invalidate caches (both app and evaluator lists)
            invalidateWorkflowsListCache()
            invalidateEvaluatorsListCache()
            if (_commitCallbacks.onQueryInvalidate) {
                void _commitCallbacks.onQueryInvalidate()
            }

            return result
        } catch (error) {
            const err = preserveResponseStatus(error, extractApiErrorMessage(error))

            if (_commitCallbacks.onError) {
                _commitCallbacks.onError(err, {revisionId, commitMessage})
            }

            return {
                success: false,
                error: err,
            }
        }
    },
)

// ============================================================================
// ARCHIVE ATOM
// ============================================================================

/**
 * Archive a single workflow revision.
 *
 * Uses `POST /workflows/revisions/{revision_id}/archive` to archive
 * only the specified revision — NOT the entire workflow artifact.
 */
export const archiveWorkflowRevisionAtom = atom(
    null,
    async (get, _set, params: WorkflowArchiveParams): Promise<WorkflowArchiveOutcome> => {
        const {revisionId, workflowId, variantId} = params

        try {
            const projectId = get(projectIdAtom)
            if (!projectId) {
                throw new Error("No project ID available")
            }

            await archiveWorkflowRevision(projectId, revisionId)

            // When variantId is provided, check if the variant still has
            // active (non-archived) revisions. If not, archive the variant
            // so it no longer appears in the entity selector dropdown.
            if (variantId) {
                try {
                    const remaining = await queryWorkflowRevisions(variantId, projectId)
                    const allRevisions = remaining.workflow_revisions ?? []
                    const activeRevisions = allRevisions.filter(
                        (r) => !r.deleted_at && r.id !== revisionId,
                    )
                    // Check if remaining revisions are only seed commits (v0)
                    // with no parameters — these are invisible to the user.
                    const userVisibleRevisions = activeRevisions.filter(
                        (r) => r.data?.parameters && Object.keys(r.data.parameters).length > 0,
                    )
                    if (userVisibleRevisions.length === 0) {
                        // Archive leftover seed revisions first
                        for (const r of activeRevisions) {
                            await archiveWorkflowRevision(projectId, r.id)
                        }
                        // Then archive the now-empty variant
                        await archiveWorkflowVariant(projectId, variantId)
                    }
                } catch (_variantErr) {
                    // Best-effort: don't fail the overall delete if variant cleanup fails
                }
            }

            const result: WorkflowArchiveResult = {
                success: true,
                revisionId,
                workflowId,
            }

            invalidateWorkflowsListCache()
            invalidateEvaluatorsListCache()
            invalidateWorkflowRevisionsByWorkflowCache(workflowId)
            // Variant-scoped revisions list backs the playground's per-variant
            // revision selector dropdown. The workflow-scoped invalidation
            // above doesn't reach it, so the deleted revision lingers in the
            // dropdown until the next manual refetch.
            if (variantId) {
                invalidateWorkflowRevisionsByVariantCache(variantId)
            }
            invalidateWorkflowCache(revisionId)

            if (_archiveCallbacks.onQueryInvalidate) {
                await _archiveCallbacks.onQueryInvalidate()
            }

            // Await selection cleanup so the replacement revision is in place
            // before the caller (delete modal) closes — prevents an empty
            // playground flash.
            if (_archiveCallbacks.onRevisionDeleted) {
                await _archiveCallbacks.onRevisionDeleted(result)
            }

            return result
        } catch (error) {
            const err = error instanceof Error ? error : new Error(String(error))

            if (_archiveCallbacks.onError) {
                _archiveCallbacks.onError(err, params)
            }

            return {
                success: false,
                error: err,
            }
        }
    },
)

// ============================================================================
// IMPERATIVE API
// ============================================================================

export async function commitWorkflowRevision(
    params: WorkflowCommitParams,
): Promise<WorkflowCommitOutcome> {
    const store = getDefaultStore()
    return store.set(commitWorkflowRevisionAtom, params)
}
